import React, { useContext, useEffect, useRef, useState } from "react";
import { PopupContentContext } from "../../../context/PopupContentContext";
import { Spinner } from "../../../useOss";
import { OrganizationInfoResponse } from "../../../api/organization/get";
import updateOrganization from "../../../api/organization/put";
import { validateOrgName } from "./utils";
import { InputErrorMessage } from "./InputErrorMessage";
import { sendButtonAnalytics, sendOrganizationAnalytics } from "../../../utils";
import { UserInfoResponse } from "../../../api/user/info";

export function OrganizationDetails({ org, user }: { org: OrganizationInfoResponse; user: UserInfoResponse }) {
    if (!org.organizationFound) return null;
    const { organizationName, role } = org;
    const [state, setState] = useState<"IDLE" | "EDITING" | "SAVING">("IDLE");
    const [inputValue, setInputValue] = useState(organizationName);
    const [inputValueError, setInputValueError] = useState<string | null>(null);

    const { showToast } = useContext(PopupContentContext);

    const inputRef = useRef<HTMLInputElement>(null);
    const isEditing = state === "EDITING";
    const disableSaving = organizationName === inputValue || !!inputValueError;

    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        const newInputValue = e.target.value;
        e.preventDefault();
        setInputValue(newInputValue);
        const result = validateOrgName(newInputValue);
        if (!result.success) {
            setInputValueError(result.error);
        } else {
            setInputValueError(null);
        }
    }

    function canChangeOrgName(userRole: typeof role) {
        return userRole === "owner";
    }

    async function handleOrgRename() {
        if (!org.organizationFound) {
            return;
        }
        setState("SAVING");
        try {
            await updateOrganization(inputValue);
            sendOrganizationAnalytics("org_name_update", {
                orgId: org.organizationId,
                author: {
                    email: user.email,
                    userId: user.userId
                }
            });
            showToast({
                status: "success",
                icon: `/static/assets/dashboard/check-rounded-green.png`,
                children: <>Organization Name Updated</>
            });
            setState("IDLE");
        } catch (e) {
            showToast({
                status: "error",
                icon: `/static/assets/dashboard/red-issue.png`,
                children: <>Organization Name Update Failed</>
            });
            setState("EDITING");
        }
    }
    function switchToEditMode() {
        setState("EDITING");
        sendButtonAnalytics("button_organization_name_edit");
    }
    useEffect(() => {
        if (isEditing) {
            // After switching to edit mode, focus on the input
            inputRef && inputRef.current && inputRef.current.focus();
        }
    }, [state]);

    function renderCTA() {
        if (!canChangeOrgName(role)) return null;
        switch (state) {
            case "EDITING":
                return (
                    <button className="save-button centered" onClick={handleOrgRename} disabled={disableSaving}>
                        Save
                    </button>
                );
            case "IDLE":
                return (
                    <div className="edit-item centered" onClick={switchToEditMode}>
                        <img src="/static/assets/dashboard/edit-icon.svg" alt="edit" />
                        <span>Edit</span>
                    </div>
                );
            case "SAVING":
                return (
                    <button className="save-button centered loading" disabled>
                        <Spinner isInline />
                    </button>
                );
        }
    }

    return (
        <>
            <div className="centered-box padded-y">
                <div className="separated-flex padded-x" style={{ minHeight: "37px" }}>
                    <span className="heading">Organization Name</span>

                    {renderCTA()}
                </div>
                <hr />
                <div className="padded-x">
                    <input
                        type="text"
                        disabled={!isEditing}
                        value={inputValue}
                        onChange={handleInputChange}
                        ref={inputRef}
                    />
                    <InputErrorMessage errorMessage={inputValueError} />
                </div>
            </div>
            <div className="component-spacing" />
        </>
    );
}
